// When an unsigned contract is old enough that someone should chase the
// customer about it.
//
// The Back Office sends no reminder of its own — logging one records a call or
// an e-mail an agent made themselves (see LogReminderDialog) — so a contract can
// sit unsigned indefinitely with nothing on the screen saying so. The backend's
// `contract_overdue` flag says a contract is late; this says what to do about it
// and stops saying it once someone has.

import { ContractTimelineEvent, OnboardingState } from "../types/onboarding";
import { awaitingSignature } from "./onboardingStates";

// A week with the customer before the first nudge, and a week between nudges:
// the same period, so an agent who chases on schedule is never asked twice.
export const CHASE_AFTER_DAYS = 7;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const REMINDER_EVENT = "REMINDER_SENT";
const DELAY_EVENT = "DELAY_REASON_LOGGED";

const parse = (iso?: string | null): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return isNaN(ms) ? null : ms;
};

const daysBetween = (from: number, to: number): number =>
  Math.floor((to - from) / MS_PER_DAY);

// Newest event of a type, or null.
const latest = (
  events: ContractTimelineEvent[],
  type: string
): string | null => {
  let best: { iso: string; ms: number } | null = null;
  (events ?? []).forEach((e) => {
    if (e.event_type !== type) return;
    const ms = parse(e.timestamp);
    if (ms === null) return;
    if (!best || ms > best.ms) best = { iso: e.timestamp, ms };
  });
  return best ? (best as { iso: string }).iso : null;
};

export const lastReminderAt = (events: ContractTimelineEvent[]): string | null =>
  latest(events, REMINDER_EVENT);

export const lastDelayReasonAt = (
  events: ContractTimelineEvent[]
): string | null => latest(events, DELAY_EVENT);

export interface ContractChase {
  // True while the contract is out, unsigned, and nobody has chased it recently.
  due: boolean;
  daysSinceSent: number | null;
  daysSinceReminder: number | null;
  // No delay reason recorded at all, so nobody has written down what the
  // hold-up is.
  reasonUnknown: boolean;
}

interface ChaseParams {
  state: OnboardingState;
  sentAt: string | null;
  events?: ContractTimelineEvent[];
  // Injected so the rule is testable without freezing the clock.
  now?: number;
}

export const contractChase = ({
  state,
  sentAt,
  events = [],
  now = Date.now(),
}: ChaseParams): ContractChase => {
  const sentMs = parse(sentAt);
  const reminderMs = parse(lastReminderAt(events));

  const daysSinceSent = sentMs === null ? null : daysBetween(sentMs, now);
  const daysSinceReminder =
    reminderMs === null ? null : daysBetween(reminderMs, now);

  // Only while the customer still owes a signature: once it is signed, an old
  // send date is history rather than something to act on.
  const waiting = awaitingSignature(state);
  const oldEnough = daysSinceSent !== null && daysSinceSent >= CHASE_AFTER_DAYS;
  // A reminder buys the same quiet period again, so chasing on schedule is
  // never nagged at.
  const chasedRecently =
    daysSinceReminder !== null && daysSinceReminder < CHASE_AFTER_DAYS;

  return {
    due: waiting && oldEnough && !chasedRecently,
    daysSinceSent,
    daysSinceReminder,
    reasonUnknown: !lastDelayReasonAt(events),
  };
};

// The line that says how long this has been sitting and what was already done
// about it — written for an agent about to pick up the phone.
export const chaseSummary = (chase: ContractChase): string => {
  const days = chase.daysSinceSent;
  const waited =
    days === null
      ? "The contract has been with the customer a while"
      : `The contract has been with the customer for ${days} days`;

  if (chase.daysSinceReminder === null) {
    return `${waited} and no reminder has been logged.`;
  }
  return `${waited}; the last reminder was logged ${chase.daysSinceReminder} days ago.`;
};
